// Los productos de un selector, livianos — ADR-2026-10-06-elegir-producto-por-tandas.
//
// `fetchUserProducts` firma la foto de TODOS los productos al traer la lista (una firma
// por producto), aunque después se vean seis. Para un selector que dibuja de a tandas
// alcanza con la lista (id, nombre, archivo de la foto principal) y firmar cada miniatura
// cuando se dibuja. Es la misma consulta y la misma firma que el selector de "Cambiar
// producto" de la pantalla de editar (`EditarImagen`): marca activa MÁS los productos sin
// marca (ADR-018), y la foto `opt/` con el `full/` de respaldo.

import { getCachedSignedUrl, setCachedSignedUrl } from '../utils/imageCache';
import { resolveMarcaActivaId } from '../utils/marcaActiva';
import { fetchInChunks } from '../utils/supabaseChunkedIn';
import { supabase } from './supabase';

export interface ProductoParaElegir {
  /** `variantes.id` (lo que va como `variante_id`). */
  id: string;
  nombre: string;
  /** El archivo de la foto principal, o `null` si no tiene. */
  imageKey: string | null;
}

export async function listarProductosParaElegir(companyId: string): Promise<ProductoParaElegir[]> {
  const marca = await resolveMarcaActivaId(companyId);
  // `string` a propósito: un ternario de literales en `.select()` rompe el parser de tipos.
  const columnas: string = marca ? 'id, nombre, productos!inner(brand_id)' : 'id, nombre';
  let consulta = supabase.from('variantes').select(columnas).eq('activo', true).eq('id_company', companyId);
  if (marca) {
    consulta = consulta.or(`brand_id.eq.${marca},brand_id.is.null`, { referencedTable: 'productos' });
  }
  const { data, error } = await consulta.order('created_at', { ascending: false });
  if (error) throw error;
  const variantes = (data ?? []) as unknown as Array<{ id: string; nombre: string }>;
  if (variantes.length === 0) return [];

  // En lotes: con catálogos grandes la lista de ids en la URL se pasa del límite.
  const fotos = await fetchInChunks<{ url: string; variante_id: string }>(
    variantes.map((v) => v.id),
    (lote) =>
      supabase
        .from('productos_imagenes')
        .select('url, variante_id')
        .eq('id_company', companyId)
        .eq('principal', true)
        .in('variante_id', lote),
  );
  const fotoDe: Record<string, string> = {};
  for (const f of fotos ?? []) if (!fotoDe[f.variante_id]) fotoDe[f.variante_id] = f.url;
  return variantes.map((v) => ({ id: v.id, nombre: v.nombre, imageKey: fotoDe[v.id] ?? null }));
}

/** La miniatura de un producto, firmada al dibujarse: `opt/` y, si no está, `full/`. */
export async function firmarMiniaturaDeProducto(
  companyId: string,
  p: ProductoParaElegir,
): Promise<string | null> {
  if (!p.imageKey) return null;
  const opt = `${companyId}/${p.id}/opt/${p.imageKey}`;
  const full = `${companyId}/${p.id}/full/${p.imageKey}`;
  const cacheada = getCachedSignedUrl(opt) || getCachedSignedUrl(full);
  if (cacheada) return cacheada;
  for (const path of [opt, full]) {
    const { data } = await supabase.storage.from('productos').createSignedUrl(path, 3600);
    if (data?.signedUrl) {
      setCachedSignedUrl(path, data.signedUrl);
      return data.signedUrl;
    }
  }
  return null;
}
